// Phase APP-2 · Admin Approvals page — KPI strip + 3 tabs (Micro Hub
// Suggestions, Packer Approvals read-only, History).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw, Search, Eye, X } from "lucide-react";
import RoleGate from "../../components/RoleGate";
import useAuthStore from "../../store/authStore";
import useTimedFetch from "../../hooks/useTimedFetch";
import StatusBadge from "../../components/hub/StatusBadge";
import AdminApprovalsKPIs from "../../components/admin/approvals/AdminApprovalsKPIs";
import ApprovalDetailDrawer from "../../components/admin/approvals/ApprovalDetailDrawer";
import RejectApprovalModal from "../../components/admin/approvals/RejectApprovalModal";
import ApproveApprovalModal from "../../components/admin/approvals/ApproveApprovalModal";

const ORANGE = "#FF6B00";
const BUILD_STAMP = "APP-1.0-r7";

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function QueueTable({ rows, loading, error, reload, isFounder, onView,
                       onApprove, onReject }) {
  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm" data-testid="queue-error">
        Couldn't load · {error.message}
        <button onClick={reload} className="ml-2 underline text-rose-700">Retry</button>
      </div>
    );
  }
  if (loading && rows.length === 0) {
    return (
      <div className="space-y-2" data-testid="queue-loading">
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse"/>)}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl py-10 text-center" data-testid="queue-empty">
        <RefreshCw className="h-6 w-6 text-slate-300 mx-auto mb-1"/>
        <div className="text-sm font-semibold text-slate-700">No pending approvals</div>
        <button onClick={reload} className="mt-2 text-xs text-[#FF6B00] hover:underline">Refresh</button>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="queue-table-wrap">
      <table className="w-full text-sm min-w-[900px]" data-testid="approvals-queue-table">
        <thead className="bg-slate-50/70">
          <tr className="text-left text-slate-500">
            <Th>Status</Th>
            <Th>Type</Th>
            <Th>Entity</Th>
            <Th>Submitted by</Th>
            <Th>Main hub</Th>
            <Th>Submitted</Th>
            <Th>Resub</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.request_id}
                data-testid={`queue-row-${r.request_id}`}
                className="hover:bg-orange-50/30">
              <td className="px-3 py-2"><StatusBadge status={r.status} size="sm"/></td>
              <td className="px-3 py-2 capitalize">{(r.approval_type || "").replace(/_/g, " ")}</td>
              <td className="px-3 py-2 font-medium" data-testid={`queue-row-${r.request_id}-entity`}>
                {r.display_name}
              </td>
              <td className="px-3 py-2">
                <div>{r.submitted_by_name}</div>
                <div className="text-[10px] text-slate-400">{r.submitted_by_role}</div>
              </td>
              <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{r.main_hub_id || "—"}</td>
              <td className="px-3 py-2 text-xs" title={r.submitted_at}>
                {relTime(r.submitted_at)}
              </td>
              <td className="px-3 py-2 text-center">{r.resubmission_count ?? 0}/1</td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex items-center gap-1">
                  <button onClick={() => onView(r)}
                          data-testid={`queue-view-${r.request_id}`}
                          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50">
                    <Eye className="h-3 w-3"/> View
                  </button>
                  {!isFounder && ["pending", "rejected_1", "resubmitted_pending"].includes(r.status) && (
                    <>
                      <button onClick={() => onApprove(r)}
                              data-testid={`queue-approve-${r.request_id}`}
                              className="text-[11px] font-semibold px-2 py-1 rounded-lg text-white"
                              style={{ backgroundColor: ORANGE }}>
                        Approve
                      </button>
                      <button onClick={() => onReject(r)}
                              data-testid={`queue-reject-${r.request_id}`}
                              className="text-[11px] font-semibold px-2 py-1 rounded-lg bg-rose-600 text-white">
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 font-semibold uppercase tracking-wider text-[10px] ${className}`}>{children}</th>;
}

function Body() {
  const user = useAuthStore((s) => s.user);
  const isFounder = (user?.role || "").toLowerCase() === "founder";
  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get("id");

  const [tab, setTab] = useState("micro");
  const [search, setSearch] = useState("");

  // Tab 1: micro hub suggestions (open queue: pending + rejected_1 + resub_pending)
  const microParams = useMemo(() => ({
    approval_type: "micro_hub_suggestion",
    search: search || undefined,
  }), [search]);
  const microQ = useTimedFetch("/approvals/queue", { params: microParams, skip: tab !== "micro" });

  // Tab 2: packer approvals — admin sees them all (founder lens)
  const packerQ = useTimedFetch("/approvals/queue",
                                   { params: { approval_type: "packer_request", search: search || undefined },
                                     skip: tab !== "packer" });

  // Tab 3: history (terminal states)
  const [histFilters, setHistFilters] = useState({});
  const histParams = useMemo(() => {
    const p = { ...histFilters };
    Object.keys(p).forEach((k) => { if (!p[k]) delete p[k]; });
    return p;
  }, [histFilters]);
  const historyQ = useTimedFetch("/approvals/history",
                                    { params: histParams, skip: tab !== "history" });

  // Drawer state — also drives deep-link
  const [openRequestId, setOpenRequestId] = useState(initialId || null);
  const [rejectFor, setRejectFor] = useState(null);
  const [approveFor, setApproveFor] = useState(null);

  // Push deep link
  useEffect(() => {
    if (openRequestId) setSearchParams({ id: openRequestId });
    else setSearchParams({});
  }, [openRequestId, setSearchParams]);

  // After approve/reject — refresh visible queue
  const onActionDone = useCallback(() => {
    if (tab === "micro") microQ.reload();
    else if (tab === "packer") packerQ.reload();
    else historyQ.reload();
  }, [tab, microQ, packerQ, historyQ]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4" data-testid="admin-approvals-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-slate-900">Approvals</h1>
        {isFounder && (
          <span data-testid="admin-approvals-founder-banner"
                className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 font-semibold">
            View as Founder · read-only
          </span>
        )}
        <span data-testid="admin-approvals-build-stamp"
              className="ml-auto text-[10px] font-mono text-slate-400">
          {BUILD_STAMP}
        </span>
      </div>

      <AdminApprovalsKPIs/>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="admin-approvals-tabs">
        {[
          { k: "micro",   label: "Micro Hub Suggestions", testid: "tab-micro" },
          { k: "packer",  label: "Packer Approvals",      testid: "tab-packer" },
          { k: "history", label: "History",                testid: "tab-history" },
        ].map((t) => (
          <button key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={t.testid}
                  data-active={tab === t.k ? "true" : "false"}
                  className={"text-sm px-3 py-2 border-b-2 -mb-px transition-colors " +
                              (tab === t.k
                                ? "border-[#FF6B00] text-[#C2410C] font-semibold"
                                : "border-transparent text-slate-500 hover:text-slate-800")}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 pb-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400"/>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search…"
                   data-testid="admin-approvals-search"
                   className="text-xs border border-slate-200 rounded-lg pl-6 pr-2 py-1 focus:border-[#FF6B00] focus:outline-none"/>
          </div>
        </div>
      </div>

      {/* Tab bodies */}
      {tab === "micro" && (
        <QueueTable
          rows={microQ.data?.rows || []}
          loading={microQ.loading}
          error={microQ.error}
          reload={microQ.reload}
          isFounder={isFounder}
          onView={(r) => setOpenRequestId(r.request_id)}
          onApprove={(r) => setApproveFor(r)}
          onReject={(r) => setRejectFor(r)}
        />
      )}
      {tab === "packer" && (
        <QueueTable
          rows={packerQ.data?.rows || []}
          loading={packerQ.loading}
          error={packerQ.error}
          reload={packerQ.reload}
          isFounder={true}   /* read-only for admin too on the packer tab */
          onView={(r) => setOpenRequestId(r.request_id)}
          onApprove={() => {}} onReject={() => {}}
        />
      )}
      {tab === "history" && (
        <>
          <HistoryFilters value={histFilters} onChange={setHistFilters}/>
          <QueueTable
            rows={historyQ.data?.rows || []}
            loading={historyQ.loading}
            error={historyQ.error}
            reload={historyQ.reload}
            isFounder={true}   /* history is read-only */
            onView={(r) => setOpenRequestId(r.request_id)}
            onApprove={() => {}} onReject={() => {}}
          />
        </>
      )}

      <ApprovalDetailDrawer
        open={!!openRequestId}
        requestId={openRequestId}
        onClose={() => setOpenRequestId(null)}
        readOnly={isFounder}
        onChanged={onActionDone}
      />
      <RejectApprovalModal
        open={!!rejectFor} onClose={() => setRejectFor(null)}
        request={rejectFor} onDone={onActionDone}/>
      <ApproveApprovalModal
        open={!!approveFor} onClose={() => setApproveFor(null)}
        request={approveFor} onDone={onActionDone}/>
    </div>
  );
}

function HistoryFilters({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const clear = () => onChange({});
  const active = Object.values(value || {}).filter((x) => x).length;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs"
         data-testid="history-filters">
      <select value={value.status || ""} onChange={(e) => set("status", e.target.value)}
              data-testid="history-status-filter"
              className="border border-slate-200 rounded-lg px-2 py-1.5">
        <option value="">All terminal statuses</option>
        <option value="approved">Approved</option>
        <option value="rejected_terminal">Rejected (terminal)</option>
        <option value="founder_override_approved">Founder override</option>
      </select>
      <select value={value.approval_type || ""} onChange={(e) => set("approval_type", e.target.value)}
              data-testid="history-type-filter"
              className="border border-slate-200 rounded-lg px-2 py-1.5">
        <option value="">All types</option>
        <option value="micro_hub_suggestion">Micro hub suggestion</option>
        <option value="packer_request">Packer request</option>
      </select>
      <input type="date" value={value.from_date || ""} onChange={(e) => set("from_date", e.target.value)}
              data-testid="history-from-filter"
              className="border border-slate-200 rounded-lg px-2 py-1.5"/>
      <input type="date" value={value.to_date || ""} onChange={(e) => set("to_date", e.target.value)}
              data-testid="history-to-filter"
              className="border border-slate-200 rounded-lg px-2 py-1.5"/>
      <input value={value.search || ""} onChange={(e) => set("search", e.target.value)}
              data-testid="history-search-filter"
              placeholder="Submitter / hub"
              className="border border-slate-200 rounded-lg px-2 py-1.5"/>
      {active > 0 && (
        <button onClick={clear} data-testid="history-clear-filters"
                className="col-span-2 sm:col-span-5 text-[#FF6B00] hover:underline inline-flex items-center gap-1 self-start">
          <X className="h-3 w-3"/> Clear all
        </button>
      )}
    </div>
  );
}

export default function AdminApprovals() {
  return (
    <RoleGate allow={["admin", "founder", "ceo"]}>
      <Body/>
    </RoleGate>
  );
}
